import React, { useState, useEffect, useRef } from 'react';
import ErpTemplate from '../../../components/ErpTemplate';
import { FaPen, FaFileInvoice, FaPlus, FaSearch, FaThumbsUp } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import { Link } from 'react-router-dom';
import api from '../../../services/api';
import Alert from '../../../components/Alert';
import { toast } from 'react-toastify';
import ReactSelect from '../../../components/ReactSelect';
import '../../../css/financeiro-parcela-pagar.css';

const TIPO_LABEL = {
    emailconvite: 'E-mail Convite',
    emailinformativa: 'E-mail Informativa',
    contrato: 'Contrato',
    convite: 'Convite',
    informativa: 'Informativa',
    termoaceite: 'Termo de Aceite',
    certificado: 'Certificado',
};

const TIPO_OPTIONS = Object.entries(TIPO_LABEL).map(([value, label]) => ({ value, label }));

// Rotulo de um cliente do modelo. O cadastro excluido aparece marcado: continua restringindo o
// modelo, e sem a marca a linha parece apontar para um cliente que nao existe mais.
const rotuloCliente = (c) => ([c.codigo, c.nome, c.municipio ? c.municipio + ' - ' + c.uf : null]
    .filter(Boolean).join(' - ') || String(c.id)) + (c.excluido ? ' (excluído)' : '');

export default function PainelEventoCarta({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const perfil = localStorage.getItem("perfil");
    const perfilPodeAlterarModeloCarta = ['admin', 'diretor', 'gerenteprojeto', 'rsvp', 'gerentersvp', 'coordenadorrsvp'].includes(perfil);
    const podeExcluirModeloCarta = ['admin', 'diretor', 'gerenteprojeto', 'rsvp'].includes(perfil);
    const [filtro, setFiltro] = useState({});
    const [buscando, setBuscando] = useState(false);
    const [eventoCartas, setEventoCartas] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const controllerRef = useRef(null);

    const buscar = () => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setBuscando(true);
        api({
            method: 'get',
            action: '/eventoCarta',
            token: token,
            params: {
                ...filtro,
                orderBy: { tipo: "asc" }
            },
            signal: controllerRef.current.signal
        })
            .then(response => {
                setEventoCartas(response.data.data);
                setSelectedIds(new Set());
                setPage(1);
                setBuscando(false);
            }).catch(err => {
                if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
                setBuscando(false);
            });
    };

    useEffect(() => {
        buscar();
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
    },
        // eslint-disable-next-line
        []);

    const excluirSelecionado = (rowId) => {
        Alert({
            icon: "error",
            confirmButton: "Sair",
            denyButton: <><FaThumbsUp size={20} /> Excluir</>,
            title: "Excluir",
            callBack: (result) => {
                if (result.isDenied) {
                    api({
                        method: 'delete',
                        action: '/eventoCarta/' + rowId,
                        token: token
                    })
                        .then(() => { buscar(); })
                        .catch(error => {
                            toast.error(error.response?.data?.error ?? 'Não foi possível excluir este modelo.');
                            buscar();
                        });
                }
            }
        });
    };

    // Modelo fixo é editável, mas não excluível — o envio cai nele quando o evento não escolhe modelo.
    const ehFixo = (rowId) => !!eventoCartas.find(c => c.id === rowId)?.fixo;

    const total = eventoCartas.length;
    const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
    const paginados = eventoCartas.slice((page - 1) * pageSize, page * pageSize);
    const selectedArr = [...selectedIds];

    const toggleSelect = (rowId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    };

    const toggleAll = () => {
        if (paginados.length > 0 && selectedIds.size === paginados.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(paginados.map(r => r.id)));
        }
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Modelos de Cartas</h1>
                    </div>
                    <div className="fin-pp__actions">
                        {perfilPodeAlterarModeloCarta && (
                            <Link to="/cadastroEventoCarta" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                                <FaPlus size={14} /> Nova Carta
                            </Link>
                        )}
                    </div>
                </div>

                <form
                    className="fin-pp__card"
                    style={{ padding: '12px 16px', marginBottom: 16 }}
                    onSubmit={(e) => { buscar(); e.preventDefault(); }}
                >
                    <div className="fin-pp__filters">
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Tipo da Carta</label>
                            <ReactSelect
                                id="tipo"
                                value={{ value: filtro.tipo, label: filtro.nomeTipo }}
                                onChange={e => setFiltro({ ...filtro, tipo: e.value, nomeTipo: e.label })}
                                options={TIPO_OPTIONS}
                                isClearable={true}
                            />
                        </div>
                        <div className="fin-pp__field" style={{ flex: 2, minWidth: 180 }}>
                            <label className="fin-pp__label">Nome da Carta</label>
                            <input
                                className="fin-pp__input"
                                style={{ paddingLeft: 14 }}
                                type="text"
                                value={filtro.nome || ''}
                                onChange={e => setFiltro({ ...filtro, nome: e.target.value })}
                                placeholder="Nome ou código da carta"
                            />
                        </div>
                        <div className="fin-pp__field" style={{ flex: 2, minWidth: 180 }}>
                            <label className="fin-pp__label">Cliente</label>
                            <ReactSelect
                                id="cliente"
                                value={{ value: filtro.cliente, label: filtro.nomeCliente }}
                                onChange={e => setFiltro({ ...filtro, cliente: e.value, nomeCliente: e.label })}
                                loadOptions={(input) => {
                                    return api({
                                        method: 'get',
                                        action: '/participante',
                                        token: token,
                                        params: {
                                            nome: input,
                                            tipoCadastro: "cliente",
                                            situacaoRegistro: "ativo",
                                        }
                                    })
                                        .then(response => {
                                            return response.data.data.map((registro) => {
                                                var label = registro.codigo +
                                                    ' - ' + registro.nome +
                                                    (registro.enderecomunicipio ? ' - ' + registro.enderecomunicipio.nome + '-' + registro.enderecomunicipio.uf : '') +
                                                    (registro.cnpj ? ' (' + registro.cnpj + ')' : '') +
                                                    (registro.cpf ? ' (' + registro.cpf + ')' : '') +
                                                    (registro.nomefantasia ? ' - ' + registro.nomefantasia : '');
                                                return { value: registro.id, label };
                                            });
                                        })
                                        .catch(() => {});
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                        <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={buscando}>
                            <FaSearch size={14} /> {buscando ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </form>

                {selectedArr.length === 1 && (
                    <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        {podeExcluirModeloCarta && !ehFixo(selectedArr[0]) && (
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--danger fin-pp__btn--sm"
                                onClick={() => excluirSelecionado(selectedArr[0])}
                            >
                                <ImCancelCircle size={14} /> Excluir
                            </button>
                        )}
                        <form
                            method="post"
                            action={process.env.REACT_APP_API_URL + "/eventoCarta/visualizarExemplo"}
                            target="_blank"
                            style={{ display: 'inline' }}
                        >
                            <input type="hidden" name="token" value={localStorage.getItem('token')} />
                            <input type="hidden" name="idCarta" value={selectedArr[0]} />
                            <button type="submit" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm">
                                <FaFileInvoice size={14} /> Visualizar
                            </button>
                        </form>
                    </div>
                )}

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>
                                        <input
                                            type="checkbox"
                                            checked={paginados.length > 0 && selectedIds.size === paginados.length}
                                            onChange={toggleAll}
                                        />
                                    </th>
                                    <th style={{ width: 100 }}></th>
                                    <th style={{ width: 80 }}>Código</th>
                                    <th style={{ width: 140 }}>Tipo</th>
                                    <th>Nome</th>
                                    <th style={{ width: 140 }}>Tipo de Evento</th>
                                    <th style={{ width: 120 }}>Formato</th>
                                    <th style={{ width: 130 }}>Nacionalidade</th>
                                    <th style={{ width: 200 }}>Clientes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buscando ? (
                                    <tr><td colSpan={9} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                ) : paginados.length === 0 ? (
                                    <tr><td colSpan={9} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map(row => (
                                    <tr key={row.id}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(row.id)}
                                                onChange={() => toggleSelect(row.id)}
                                            />
                                        </td>
                                        <td>
                                            {row.bancoDados !== undefined && perfilPodeAlterarModeloCarta && (
                                                <Link
                                                    to={{
                                                        pathname: "/cadastroEventoCarta/" + row.id,
                                                        state: { evento: id }
                                                    }}
                                                    className="fin-pp__btn fin-pp__btn--sm"
                                                    style={{ fontSize: 12 }}
                                                >
                                                    <FaPen size={12} /> Alterar
                                                </Link>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right' }} className="fin-pp__cell-muted">{row.codigo ?? ""}</td>
                                        <td className="fin-pp__cell-muted">{TIPO_LABEL[row.tipo] ?? ""}</td>
                                        <td className="fin-pp__cell-strong">
                                            {row.nome ?? ""}
                                            {row.fixo && (
                                                <span
                                                    className="fin-pp__cell-muted"
                                                    title="Modelo usado quando o evento não tem modelo próprio. Pode ser editado, não pode ser excluído."
                                                    style={{ marginLeft: 8, fontSize: 11, border: '1px solid var(--fin-bd)', borderRadius: 10, padding: '1px 8px' }}
                                                >
                                                    Padrão
                                                </span>
                                            )}
                                        </td>
                                        <td className="fin-pp__cell-muted">{row.eventoTipo ?? ""}</td>
                                        <td className="fin-pp__cell-muted">{row.eventoTipoRealizacao ?? ""}</td>
                                        <td className="fin-pp__cell-muted">{row.eventoNacionalidade ?? ""}</td>
                                        <td className="fin-pp__cell-muted">
                                            {/* Vazio = modelo vale para todos os clientes. */}
                                            {(row.clientes ?? []).map(c => (
                                                <div key={c.id}>{rotuloCliente(c)}</div>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{total} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage(p => p + 1)}>Próxima</button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
